/**
 * The one place Mus vocabulary is rendered (frontend.md §5.4).
 *
 * `translate="no"` is the load-bearing attribute: without it Chrome's page
 * translator turns "órdago" into "outrage" and "mano" into "hand" on the English
 * UI, silently undoing the decision that these words are never translated.
 * `lang="es"` tells the same machinery — and a screen reader — which voice to
 * use for a Spanish word inside an English sentence.
 */

import { useTranslation } from 'react-i18next';

import type { MusTermKey } from '@ordago/shared';

export interface TermProps {
  k: MusTermKey;
  /** Selects the plural form for the keys that have one (piedra, seña…). */
  count?: number;
  // Explicitly `| undefined`: `exactOptionalPropertyTypes` is on, and a CSS
  // module lookup is `string | undefined` under `noUncheckedIndexedAccess`.
  className?: string | undefined;
}

export function Term({ k, count, className }: TermProps) {
  const { t } = useTranslation('mus');
  return (
    <span
      className={['mus-term', className].filter(Boolean).join(' ')}
      lang="es"
      translate="no"
      data-term={k}
    >
      {count === undefined ? t(k) : t(k, { count })}
    </span>
  );
}

/**
 * The same vocabulary as a plain string, for the places a `<span>` cannot go —
 * an `aria-label`, a `title`, or a term interpolated into a sentence frame.
 */
export function useMusTerm(): (key: MusTermKey, count?: number) => string {
  const { t } = useTranslation('mus');
  return (key, count) => (count === undefined ? t(key) : t(key, { count }));
}
